<div class="xx-external-save p-3">

    <div class="mb-3">
        <h2 class="m-0">Formular mit externem Speichern</h2>
        <small class="text-color-secondary">
            Der Speichern-Button gehört der aufrufenden Komponente. Das Formular schreibt trotzdem
            selbst — es meldet nur, ob sich etwas geändert hat und ob die Daten valide und vollständig
            sind. Kein Feld-Check wird hier nachgebaut.
        </small>
    </div>

    <!-- ── page controls ───────────────────────────────────────────────────────────── -->
    <div class="xx-es-box p-3 mb-3 border-round-lg">
        <div class="flex flex-wrap align-items-end gap-3">

            <div class="flex flex-column gap-1" style="min-width: 24rem">
                <label class="text-sm text-color-secondary">Entität</label>
                <p-select [options]="objectTypeOptions" optionLabel="label" optionValue="value"
                          [(ngModel)]="objectType" (onChange)="handleObjectTypeChange()"
                          styleClass="w-full"></p-select>
            </div>

            <div class="flex flex-column gap-1" style="width: 12rem">
                <label class="text-sm text-color-secondary">Objekt-ID (leer = Anlegen)</label>
                <p-inputNumber [(ngModel)]="objectId" [useGrouping]="false" [showButtons]="false"
                               (onBlur)="handleObjectIdChange()" styleClass="w-full"></p-inputNumber>
            </div>

            <div class="flex align-items-center gap-2">
                <p-toggleSwitch [(ngModel)]="hideDefaultActions"></p-toggleSwitch>
                <span class="text-sm">hideDefaultActions</span>
            </div>

            <div class="flex align-items-center gap-2">
                <p-toggleSwitch [(ngModel)]="disabled"></p-toggleSwitch>
                <span class="text-sm">disabled</span>
            </div>

            <div class="flex align-items-center gap-2">
                <p-toggleSwitch [(ngModel)]="readonlyForm"></p-toggleSwitch>
                <span class="text-sm">readonly</span>
            </div>

            <p-button label="Formular neu aufbauen" icon="fa-light fa-arrows-rotate"
                      severity="secondary" [outlined]="true"
                      (onClick)="handleRebuild()"></p-button>
        </div>
    </div>

    <div class="grid">

        <!-- ── left: the embedded form ─────────────────────────────────────────────── -->
        <div class="col-12 lg:col-7">
            <div class="xx-es-box p-3 border-round-lg h-full">

                <div class="flex align-items-center justify-content-between mb-2">
                    <span class="font-semibold">Formular</span>
                    <p-tag [value]="state?.createMode === false ? 'Bearbeiten' : 'Anlegen'"
                           [severity]="state?.createMode === false ? 'info' : 'success'"></p-tag>
                </div>

                <!--
                  A single-element @for tracked by the counter: bumping formInstance changes the
                  track key, so Angular DESTROYS and recreates the component. That is what proves the
                  published state starts clean again instead of being carried over.
                -->
                @for (instance of [formInstance]; track instance) {
                    <!--
                      AI: CRUD Object Component — CAPTURE ONLY. The footer is suppressed, the write is
                      triggered from this page via the #crudObject handle.
                    -->
                    <mvs-crud-object #crudObject
                                     [objectType]="objectType"
                                     [objectId]="objectId"
                                     [hideDefaultActions]="hideDefaultActions"
                                     [disabled]="disabled"
                                     [readonly]="readonlyForm"
                                     (onFormStateChange)="handleFormStateChange($event)"
                                     (onFormDirty)="handleFormDirty($event)"
                                     (onFormSubmitStateChange)="handleFormSubmitStateChange($event)"
                                     (onFormChange)="handleFormChange($event)"
                                     (onChangedObject)="handleChangedObject($event)"
                                     (onObjectChangeError)="handleObjectChangeError($event)">
                    </mvs-crud-object>
                }

                <!-- ── the caller-owned footer ─────────────────────────────────────── -->
                <div class="flex flex-wrap justify-content-end align-items-center gap-2 pt-3 mt-3 border-top-1 surface-border">

                    <small class="text-color-secondary mr-auto">
                        Diese Buttons gehören der Seite, nicht dem Formular.
                    </small>

                    <p-button label="save() ohne Prüfung" icon="fa-light fa-triangle-exclamation"
                              severity="secondary" [outlined]="true"
                              (onClick)="handleForcedSave()"></p-button>

                    <p-button [label]="saveLabel" icon="fa-light fa-check"
                              severity="success"
                              [loading]="state?.busy === true"
                              [disabled]="state?.submittable !== true"
                              (onClick)="handleSave()"></p-button>
                </div>

                @if (lastTriggerResult) {
                    <div class="xx-es-code mt-2 p-2 border-round text-sm">{{ lastTriggerResult }}</div>
                }

                @if (resultMessage) {
                    <p-message severity="success" [text]="resultMessage" styleClass="w-full mt-2"></p-message>
                }

                @if (errorMessage) {
                    <p-message severity="error" [text]="errorMessage" styleClass="w-full mt-2"></p-message>
                }
            </div>
        </div>

        <!-- ── right: live state + event log ───────────────────────────────────────── -->
        <div class="col-12 lg:col-5">

            <div class="xx-es-box p-3 border-round-lg mb-3">

                <div class="font-semibold mb-2">Zustand (onFormStateChange)</div>

                @if (!state) {
                    <span class="text-color-secondary text-sm">
                        Noch kein Zustand veröffentlicht — das Formular lädt sein Template.
                    </span>
                } @else {
                    <div class="flex flex-wrap gap-2">
                        <p-tag [value]="'dirty: ' + state.dirty"
                               [severity]="state.dirty ? 'warn' : 'secondary'"></p-tag>
                        <p-tag [value]="'valid: ' + state.valid"
                               [severity]="state.valid ? 'success' : 'danger'"></p-tag>
                        <p-tag [value]="'complete: ' + state.complete"
                               [severity]="state.complete ? 'success' : 'danger'"></p-tag>
                        <p-tag [value]="'busy: ' + state.busy"
                               [severity]="state.busy ? 'warn' : 'secondary'"></p-tag>
                        <p-tag [value]="'disabled: ' + state.disabled" severity="secondary"></p-tag>
                        <p-tag [value]="'readonly: ' + state.readonly" severity="secondary"></p-tag>
                        <p-tag [value]="'createMode: ' + state.createMode" severity="info"></p-tag>
                        <p-tag [value]="'submittable: ' + state.submittable"
                               [severity]="state.submittable ? 'success' : 'danger'"></p-tag>
                    </div>

                    <!-- The two narrow outputs that existed before, so any drift is visible -->
                    <div class="text-sm text-color-secondary mt-3">
                        onFormDirty: <span class="font-semibold">{{ dirtyOutput }}</span>
                        &nbsp;·&nbsp;
                        onFormSubmitStateChange: <span class="font-semibold">{{ submitStateOutput }}</span>
                    </div>
                }
            </div>

            <div class="xx-es-box p-3 border-round-lg mb-3">
                <div class="flex align-items-center justify-content-between mb-2">
                    <span class="font-semibold">Nutzlast (onFormChange)</span>
                    <small class="text-color-secondary">Das würde gespeichert.</small>
                </div>

                @if (lastFormValue) {
                    <div class="xx-es-code p-2 border-round text-sm">{{ lastFormValue | json }}</div>
                } @else {
                    <span class="text-color-secondary text-sm">Noch keine Änderung erfasst.</span>
                }
            </div>

            <div class="xx-es-box p-3 border-round-lg">

                <div class="flex align-items-center justify-content-between mb-2">
                    <span class="font-semibold">Ereignisse</span>
                    <p-button label="Leeren" severity="secondary" [text]="true" size="small"
                              (onClick)="handleClearLog()"></p-button>
                </div>

                @if (!eventLog.length) {
                    <span class="text-color-secondary text-sm">Noch nichts passiert.</span>
                } @else {
                    <div class="xx-es-log">
                        @for (entry of eventLog; track entry.seq) {
                            <div class="flex gap-2 align-items-baseline py-1 border-bottom-1 surface-border text-sm">
                                <span class="text-color-secondary xx-es-time">{{ entry.time }}</span>
                                <p-tag [value]="entry.name" [severity]="entry.severity"></p-tag>
                                <span class="flex-1 xx-es-detail">{{ entry.detail }}</span>
                            </div>
                        }
                    </div>
                }
            </div>
        </div>
    </div>
</div>
